import React from 'react';
import { motion } from 'framer-motion';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { formatPrice } from '../utils/formatPrice';
import { getEarningsData, getDashboardStats, getPropertyStats } from '../utils/dashboardData';
import { ChartBarIcon, CurrencyDollarIcon, UsersIcon, HomeIcon } from '@heroicons/react/24/outline';
import PageWrapper from '../components/layout/PageWrapper';
import type { ChartDataPoint } from '../types/Dashboard';

const AnalyticsPage: React.FC = () => {
  const earningsData = getEarningsData();
  const dashboardStats = getDashboardStats();
  const propertyStats = getPropertyStats();

  const lineChartData = [
    {
      id: 'earnings',
      color: '#FF385C',
      data: earningsData.map((d: any) => ({
        x: d.month,
        y: d.earnings
      }))
    }
  ];

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatPrice(dashboardStats.totalEarnings),
      change: '+12.5%',
      icon: CurrencyDollarIcon,
      color: 'text-emerald-500'
    },
    {
      title: 'Total Bookings',
      value: '156',
      change: '+8.2%',
      icon: ChartBarIcon,
      color: 'text-blue-500'
    },
    {
      title: 'Active Guests',
      value: dashboardStats.totalGuests.toString(),
      change: '+15.3%',
      icon: UsersIcon,
      color: 'text-purple-500'
    },
    {
      title: 'Properties',
      value: propertyStats.length.toString(),
      change: '+2.4%',
      icon: HomeIcon,
      color: 'text-amber-500'
    }
  ];

  return (
    <PageWrapper>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Analytics Overview</h1>
          <p className="text-gray-500 mt-1">Track your property performance and revenue</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.color} bg-opacity-10`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                <span className="text-sm font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                  {stat.change}
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-500">{stat.title}</h3>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h2>
            <div style={{ height: 400 }}>
              <ResponsiveLine
                data={lineChartData}
                margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                curve="monotoneX"
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  format: value => `$${value/1000}k`
                }}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableGridX={false}
                enableArea={true}
                areaOpacity={0.1}
                colors={['#FF385C']}
                theme={{
                  axis: {
                    ticks: {
                      text: {
                        fontSize: 12,
                        fill: '#6B7280'
                      }
                    }
                  },
                  grid: {
                    line: {
                      stroke: '#E5E7EB',
                      strokeWidth: 1
                    }
                  }
                }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Property Performance</h2>
            <div className="space-y-4">
              {propertyStats.map((property: any, index: number) => (
                <div key={property.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{property.name}</h3>
                    <span className="text-sm text-gray-500">${property.totalEarnings.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-airbnb-red rounded-full h-2"
                      style={{ width: `${property.occupancyRate}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-gray-500">Occupancy Rate</span>
                    <span className="font-medium text-gray-900">{property.occupancyRate}%</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </PageWrapper>
  );
};

export default AnalyticsPage;